import React, { useEffect, useRef, useState } from 'react'
import { LuArrowLeft, LuX } from 'react-icons/lu'

import { useTagCreate } from '@/feature/tags/hooks/useTagCreate'
import { useTagToggle } from '@/feature/tags/hooks/useTagToggle'

import { ColorPicker } from '../ColorPicker'
import { AssetTag } from '../TableHeaderTags/TableHeaderTags.types'
import styles from './TagsPopout.module.css'
import { CreateTagScreenProps } from './TagsPopout.types'

export const CreateTagScreen: React.FC<CreateTagScreenProps> = ({
  onBack,
  onTagCreated,
  assetId,
}) => {
  const [tagName, setTagName] = useState('')
  const [selectedColor, setSelectedColor] = useState('#FFFFFF')
  const [assignmentError, setAssignmentError] = useState<string | null>(null)
  const [createdTag, setCreatedTag] = useState<AssetTag | null>(null)
  const assignmentErrorRef = useRef<HTMLDivElement>(null)
  const { handleTagToggle } = useTagToggle()
  const { createTag, isCreating, validationError } = useTagCreate({
    onSuccess: async (newTag) => {
      // Reset assignment error
      setAssignmentError(null)

      let hasAssignmentError = false

      // If we have an assetId, try to assign the new tag to the asset
      if (assetId) {
        // Toggle the tag on (true means we want to assign it to the asset)
        handleTagToggle(newTag, true, () => {
          // Handle the case where assignment fails due to 10-tag limit
          setAssignmentError('Unable to auto-assign tag to asset. 10 Tag limit reached.')
          setCreatedTag(newTag)
          hasAssignmentError = true
        })
      }

      // Only call onTagCreated if there was no assignment error
      // This prevents the component from navigating away before the user sees the error
      if (!hasAssignmentError) {
        onTagCreated(newTag)
      }
    },
  })

  const handleCreateTag = async () => {
    if (!tagName.trim()) return

    await createTag(tagName, selectedColor)
  }

  const onTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    // Only allow alphanumeric characters and spaces
    const sanitized = newValue.replace(/[^a-zA-Z0-9 ]/g, '')
    setTagName(sanitized)
  }

  const handleContinueAfterError = () => {
    if (createdTag) {
      onTagCreated(createdTag)
    }
  }

  // Scroll assignment error into view when it appears
  useEffect(() => {
    if (assignmentError && assignmentErrorRef.current) {
      assignmentErrorRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [assignmentError])

  return (
    <>
      <div className={styles.popoutHeader}>
        <button
          className={styles.backButton}
          onClick={(e) => {
            e.stopPropagation()
            onBack()
          }}
          data-testid="back-button"
        >
          <LuArrowLeft size={16} />
        </button>
        <div className={styles.headerTitle} data-testid="header-title">
          Create New Tag
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.actionButton}
            onClick={(e) => {
              e.stopPropagation()
              onBack()
            }}
            data-testid="close-button"
          >
            <LuX size={16} />
          </button>
        </div>
      </div>

      <div className={styles.createTagContent}>
        <div className={styles.inputGroup}>
          <label htmlFor="tagName">Tag Name</label>
          {validationError && <div className={styles.errorLine}>{validationError}</div>}
          {assignmentError && (
            <div ref={assignmentErrorRef} className={styles.errorLine}>
              {assignmentError}
            </div>
          )}
          <input
            id="tagName"
            type="text"
            value={tagName}
            onChange={(e) => onTagInputChange(e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCreateTag()
              }
            }}
            placeholder="Enter tag name"
            className={styles.tagNameInput}
            data-testid="tag-name-input"
            autoFocus
          />
        </div>

        <ColorPicker value={selectedColor} onChange={setSelectedColor} dataTestId="color-picker" />
      </div>

      {assignmentError && createdTag ? (
        <button
          className={styles.createNewButton}
          onClick={handleContinueAfterError}
          data-testid="continue-button"
        >
          Only Create Tag
        </button>
      ) : (
        <button
          className={styles.createNewButton}
          onClick={handleCreateTag}
          disabled={!tagName.trim() || isCreating}
          data-testid="create-button"
        >
          {isCreating ? 'Creating...' : 'Create New Tag'}
        </button>
      )}
    </>
  )
}
